import {
  DATA_SIS_GRAPH_MAX_FIELDS,
  type GraphSisFieldItem,
  type GraphSisParams,
} from './sis/types';

/**
 * API 取得対象の項目を解決する。
 * field / fields がない初回表示は undefined を返し、API 既定項目を維持する。
 */
export const resolveDataSisGraphFields = (
  params: GraphSisParams
): string[] | undefined => {
  if (params.fields && params.fields.length > 0) {
    return params.fields.slice(0, DATA_SIS_GRAPH_MAX_FIELDS);
  }
  if (params.field) {
    return [params.field];
  }
  return undefined;
};

export const resolveDataSisGraphFieldsForSearch = (
  existingParams: GraphSisParams | undefined
): string[] | undefined =>
  existingParams ? resolveDataSisGraphFields(existingParams) : undefined;

export const resolveDataSisGraphFieldsFromFavorite = (
  params: GraphSisParams
): string[] | undefined => resolveDataSisGraphFields(params);

export const buildDataSisGraphFieldsAfterAddEmptySlot = (
  currentFields: readonly string[],
  maxFields: number = DATA_SIS_GRAPH_MAX_FIELDS
): string[] =>
  currentFields.length >= maxFields
    ? currentFields.slice(0, maxFields)
    : [...currentFields, ''];

export const buildDataSisGraphFieldsAfterRemove = (
  currentFields: readonly string[],
  index: number,
  maxFields: number = DATA_SIS_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= currentFields.length || currentFields.length <= 1) {
    return currentFields.slice(0, maxFields);
  }
  return currentFields
    .filter((_, fieldIndex) => fieldIndex !== index)
    .slice(0, maxFields);
};

export const buildDataSisGraphFieldsAfterIndexChange = (
  currentFields: readonly string[],
  index: number,
  field: string,
  maxFields: number = DATA_SIS_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= maxFields) {
    return currentFields.slice(0, maxFields);
  }
  const slotCount = Math.min(
    Math.max(currentFields.length, index + 1),
    maxFields
  );
  return Array.from({ length: slotCount }, (_, slotIndex) =>
    slotIndex === index ? field : currentFields.at(slotIndex) ?? ''
  );
};

export const normalizeDataSisGraphFieldsForFetch = (
  fields: readonly string[],
  maxFields: number = DATA_SIS_GRAPH_MAX_FIELDS
): string[] => fields.filter((field) => field !== '').slice(0, maxFields);

export const shouldRefetchDataSisGraphFields = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean =>
  normalizeDataSisGraphFieldsForFetch(previousFields).join(',') !==
  normalizeDataSisGraphFieldsForFetch(nextFields).join(',');

const areRemainingFieldsInOrder = (
  remainingFields: readonly string[],
  originalFields: readonly string[]
): boolean =>
  remainingFields.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, field) => {
      if (!state.isMatch) {
        return state;
      }
      const relativeIndex = originalFields
        .slice(state.searchIndex)
        .findIndex((candidate) => candidate === field);
      return relativeIndex === -1
        ? { searchIndex: state.searchIndex, isMatch: false }
        : {
            searchIndex: state.searchIndex + relativeIndex + 1,
            isMatch: true,
          };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

export const isDataSisGraphFieldsRemovalOnly = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrevious =
    normalizeDataSisGraphFieldsForFetch(previousFields);
  const normalizedNext = normalizeDataSisGraphFieldsForFetch(nextFields);
  return (
    normalizedNext.length < normalizedPrevious.length &&
    areRemainingFieldsInOrder(normalizedNext, normalizedPrevious)
  );
};

export const resolveDataSisGraphItemForFieldSlot = (
  graphList: readonly GraphSisFieldItem[],
  fieldSlots: readonly string[],
  slotIndex: number
): GraphSisFieldItem | undefined => {
  const field = fieldSlots.at(slotIndex);
  if (!field) {
    return undefined;
  }
  const graphListIndex = fieldSlots
    .slice(0, slotIndex)
    .filter((slotField) => slotField !== '').length;
  const itemByIndex = graphList.at(graphListIndex);
  return itemByIndex?.field === field
    ? itemByIndex
    : graphList.find((item) => item.field === field);
};

export const buildDataSisGraphListForFields = (
  currentGraphList: readonly GraphSisFieldItem[],
  fields: readonly string[]
): GraphSisFieldItem[] => {
  const graphByField = new Map(
    currentGraphList.map((item) => [item.field, item])
  );
  return fields.flatMap((field) => {
    const item = graphByField.get(field);
    return item ? [item] : [];
  });
};

export const mergeDataSisGraphFieldSlots = (
  previousSlots: readonly string[],
  graphList: readonly Pick<GraphSisFieldItem, 'field'>[],
  maxFields: number = DATA_SIS_GRAPH_MAX_FIELDS
): string[] => {
  if (graphList.length === 0) {
    return [];
  }
  const fieldsFromGraph = graphList.map((item) => item.field);
  const normalizedPrevious = normalizeDataSisGraphFieldsForFetch(
    previousSlots,
    maxFields
  );
  if (normalizedPrevious.join(',') === fieldsFromGraph.join(',')) {
    return previousSlots.slice(0, maxFields);
  }
  if (
    normalizedPrevious.length > fieldsFromGraph.length &&
    fieldsFromGraph.every(
      (field, index) => normalizedPrevious.at(index) === field
    )
  ) {
    return previousSlots.slice(0, maxFields);
  }
  const trailingEmptySlots = previousSlots
    .slice(fieldsFromGraph.length)
    .filter((field) => field === '');
  return [...fieldsFromGraph, ...trailingEmptySlots].slice(0, maxFields);
};

export { syncGraphSlotKeys } from './graphSlotKeys';
